import { EditOutlined, HistoryOutlined } from '@ant-design/icons';
import { Button, Space, Tooltip, Typography } from 'antd';
import cn from 'classnames';
import { Link } from 'react-router-dom';

import { Avatar } from '@app/components/Avatar';
import {
  ACTIONS,
  AVATAR,
  CONTACTS,
  KEY,
  LAST_VISIT,
  NAME,
  RIGHTS,
} from '@app/constants';
import type { NAnalytics } from '@app/types';
import { shortName } from '@app/utils';

export const adaptDataToTable = ({
  data,
  handleEdit,
  handleHistoryIconClick,
  hasUserRightToEdit,
}: {
  data: NAnalytics.ModelUserLite[];
  handleEdit: (id: number) => void;
  handleHistoryIconClick: (id: number) => void;
  hasUserRightToEdit: boolean;
}) => {
  return data.map((user: NAnalytics.ModelUserLite, index: number) => {
    const {
      Photo,
      NameFirst,
      NameLast,
      Phone,
      Email,
      LastEntryS,
      Id,
      IsDeleted,
      AccessRightsDescription,
      Position,
      Description,
      IsFakeEmail,
      IsFakePhone,
    } = user;

    const shortedUserName = shortName(`${NameLast} ${NameFirst}`, true);

    return {
      [KEY]: String(index),
      [AVATAR]: (
        <Avatar
          src={Photo}
          nameFirst={NameFirst}
          nameLast={NameLast}
          className="avatar"
          size={60}
        />
      ),
      [NAME]: (
        <span className={cn({ 'deleted-cell': IsDeleted })}>
          <Link to={`/users/edit/?id=${Id}&key=1`} data-testid="user-name">
            {shortedUserName}
          </Link>
          {Position && (
            <span>
              <br />
              <Typography.Text type="secondary" data-testid="user-position">
                {Position}
              </Typography.Text>
            </span>
          )}
          {IsDeleted && (
            <span style={{ color: 'red' }} data-testid="in-archive-user">
              <br />В архиве
            </span>
          )}
          {Description && !IsDeleted && (
            <div className="additional-info">
              <Typography.Text type="secondary">{Description}</Typography.Text>
            </div>
          )}
        </span>
      ),
      [CONTACTS]: (
        <p className={cn({ 'deleted-cell': IsDeleted })}>
          {!IsFakePhone && Phone && (
            <>
              <span data-testid="user-phone">{`+${Phone}`}</span>
              <br />
            </>
          )}
          {!IsFakeEmail && <span data-testid="user-email">{Email}</span>}
        </p>
      ),
      [RIGHTS]: (
        <span
          className={cn({ 'deleted-cell': IsDeleted })}
          data-testid="user-rights"
        >
          {AccessRightsDescription || '–'}
        </span>
      ),
      [LAST_VISIT]: (
        <span
          className={cn({ 'deleted-cell': IsDeleted })}
          data-testid="user-last-visit"
        >
          {LastEntryS}
        </span>
      ),
      [ACTIONS]: (
        <Space>
          <Tooltip title="История пользователя">
            <Button
              data-testid="history-icon"
              icon={<HistoryOutlined />}
              onClick={() => handleHistoryIconClick(Id)}
            />
          </Tooltip>
          {!IsDeleted && hasUserRightToEdit && (
            <Tooltip title="Редактировать">
              <Button
                icon={<EditOutlined />}
                onClick={() => handleEdit(Id)}
                data-testid="edit-icon"
              />
            </Tooltip>
          )}
        </Space>
      ),
    };
  });
};
